import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import ReactDOM from 'react-dom/client';
import {
  RouterProvider,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { GlobalStyles } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { initAuth } from '@hssoft/auth-web-shared-lib';
import { AuthShell } from './shared/auth/authShell.component';
import { SnackbarProvider } from 'notistack';
import { useEffect } from 'react';
import { environment } from './environment.ts';
import './i18n/init.ts';
import { routes } from './routes/routes';
import { appInit } from './serviceWorker/appInit.ts';
import { SplashScreen } from './shared/components/loading/splashScreen.component';
import SnackbarComponent from './shared/components/snackbar/snackbar.component.tsx';
import { SnackBarService } from './shared/components/snackbar/snackbarService.component.tsx';
import { checkAndIncrementSentryErrorCount } from './shared/helpers/sentryErrorCountTracker/sentryErrorCountTracker.helper.ts';
import { startVersionPolling } from './shared/services/versionCheck/versionCheck.service.ts';
import { Theme } from './shared/styles/theme/theme';
import { isAmplifyUserAttrsLengthBug } from './shared/helpers/sentryIgnoreCaces/sentryIgnoreCaces.helper.ts';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';

const appEnv = import.meta.env.VITE_REACT_APP_ENV as keyof typeof environment;
const release = import.meta.env.VITE_REACT_APP_RELEASE as string; // Release date day-month-year
const env = environment[appEnv];

LicenseInfo.setLicenseKey(env.muiLicenseKey);

// Configure Amplify (Auth + Storage) via the shared auth library.
// Must run before any provider (AuthProvider, GraphQL client, file uploads) reads the Amplify config.
initAuth({ Auth: env.aws.Auth, Storage: env.aws.Storage }, { cookieStorage: env.cookie });

if (appEnv === 'production') {
  Sentry.init({
    dsn: 'https://bd5ccc4ba7a44ba5b4959d5ef4e20b8d@o4504242163613696.ingest.sentry.io/4504242167414784',
    release,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        unblock: ['div, [data-sentry-unblock]'],
        unmask: ['input, [data-sentry-unmask]'],
      }),
    ],
    beforeSend(event, hint) {
      // * Ignore errors based on level
      if (event.level === 'info') {
        return null;
      }

      // * Ignore errors based on traсking their events amount
      const errorMessage = event.message || event.exception?.values?.[0]?.value || 'Unknown error';
      const errorMessagesToTrack = ['DB is in updating process.']; // Add more messages to track other errors
      const matchedTrackedError = errorMessagesToTrack.find((msg) => errorMessage.startsWith(msg));
      const shouldIgnoreEvent =
        matchedTrackedError &&
        !checkAndIncrementSentryErrorCount(matchedTrackedError as string, 30); // Regulating threshold of sentry errors
      if (shouldIgnoreEvent) {
        return null;
      }

      // * Ignore errors
      const error = hint.originalException as Error;
      const notInteresting =
        error?.name?.match?.(
          /^(Loading chunk)|(ChunkLoadError)|(Large Render Blocking Asset)|(SecurityError)$/gi,
        ) || isAmplifyUserAttrsLengthBug(event);

      if (notInteresting) {
        return null;
      }

      // Suppress errors explicitly flagged as expected by the GraphQL client.
      // gqlClient sets __sentryIgnore = true on transient 401s that survive
      // a one-shot token refresh — these are session-expired UX events, not
      // bugs, and were generating Sentry-driven Jira tickets (e.g. WA-3514,
      // WA-3515). The user is routed back to login via the normal error
      // pipeline; Sentry has nothing actionable to capture.
      if ((hint.originalException as { __sentryIgnore?: boolean })?.__sentryIgnore) {
        return null;
      }

      // * Ignore errors based on exception values
      const ignoreList = [
        {
          exceptionType: 'TypeError',
          exceptionValue: 'Script https://wawiassist.hssoft.com/service-worker.js load failed',
        },
        {
          exceptionType: 'Error',
          exceptionValuePattern: /^No row with id #\d+ found$/,
        },
        // Add more error patterns to ignore
      ];
      const exception = event.exception?.values?.[0];
      const shouldIgnore = ignoreList.some((ignoreCondition) => {
        const typeMatches =
          ignoreCondition.exceptionType && exception?.type === ignoreCondition.exceptionType;

        if (!typeMatches) {
          return false;
        }

        // Check exact value match
        if (ignoreCondition.exceptionValue) {
          return exception?.value === ignoreCondition.exceptionValue;
        }

        // Check pattern match
        if (ignoreCondition.exceptionValuePattern) {
          return ignoreCondition.exceptionValuePattern.test(exception?.value || '');
        }

        return false;
      });

      if (shouldIgnore) {
        return null;
      }

      return event;
    },
    // * Ignor issues by message
    ignoreErrors: [
      /^The user is not authenticated$/,
      /^No current user$/,
      /^Load failed$/,
      'Customer number already exists ',
      'ResizeObserver loop limit exceeded',
      /^PDF reports with id \d+ with last changes are not generated/,
      // WA-3573: deleting an entity still referenced elsewhere is an expected
      // FK/linked-records constraint, not a bug. One pattern covers every
      // entity type (production group, article list, …); it is deliberately
      // not end-anchored because the backend appends a varying `Request id: "…"`.
      /could not be deleted because it is linked with some records/,
    ],
    tracesSampleRate: 1.0,
  });

  const postHogProjectKey = 'phc_NlzTywzu7zDuhJsfYgUq9Zoo2DCgQVXex1PxiSdJzOb';
  posthog.init(postHogProjectKey, {
    api_host: 'https://eu.i.posthog.com',
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    cross_subdomain_cookie: false,
    autocapture: true,
    disable_session_recording: false,
    capture_pageview: true,
    capture_pageleave: true,
  });
}

const AppStructure = (
  <>
    <SplashScreen />
    <CssBaseline />
    <GlobalStyles
      styles={(theme) => ({
        html: { fontSize: theme.typography.fontSize, fontFamily: theme.typography.fontFamily },
      })}
    />
    <ThemeProvider theme={Theme}>
      <SnackbarProvider Components={{ default: SnackbarComponent }}>
        <SnackBarService />
        <AuthShell>
          <RouterProvider router={routes} />
        </AuthShell>
      </SnackbarProvider>
    </ThemeProvider>
  </>
);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  posthog.__loaded ? (
    <PostHogProvider client={posthog}>{AppStructure}</PostHogProvider>
  ) : (
    AppStructure
  ),
);

appInit();
startVersionPolling();
